import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik, getIn } from 'formik';
import usePfqAxios from './usePfqAxios';
import useCaseFieldUpdate from './useCaseFieldUpdate';
import usePolling from './usePolling';
import { buildVirtualUserPayload } from '../utils/easyModePayload';
import Constants from '../Constants';
import { errorToast, toaster, successToast } from '../utils/toast';
import useEmbedded from './useEmbedded';
import { useProductContext } from '../contexts/ProductContext';
import { useConfirm } from '../contexts/ConfirmContext';
import {
  LIABILITY_FILES_KEY,
  CREATE_REQUEST_STATUS,
  REQUEST_TYPES,
  deriveMedicalProvidersInputMode
} from '@demandproai/shared';

// Form components
import LiabilityAnalysisForm from '../pages/admin/cases/component/LiabilityAnalysisForm';
import MedicalProviderForm from '../pages/admin/cases/component/MedicalProviderForm';
import PainAndSufferingAnalysisForm from '../pages/admin/cases/component/PainAndSufferingAnalysisForm';
import EconomicDamageAnalysisForm from '../pages/admin/cases/component/EconomicDamageAnalysisForm';

// Redux actions
import {
  preProcessedMedicalRecordsData,
} from '../redux/actions/medicalRecords';
import {
  presignedUrlGenerationStart,
  presignedUrlGenerationComplete
} from '../redux/actions/fileUpload';

// Utils and constants
import {
  checkIsfileUploading,
  generateRandomIds,
  getDefendantId,
  setDefendantId,
  isSettledRequest,
} from "../utils/helper";
import {
  getIsClientPendingAiResolution,
  getPendingAiResolutionSections
} from '../utils/medicalProviderHelpers';
import {
  getDefaultClient,
  getDefaultCaseInfo,
  getDefaultDefendant,
  defaultMedicalProviders,
  defaultLiability
} from '../utils/formSchema';
import {
  FORM_STEPS,
  isLastStep
} from '../constants/formSteps';
import CaseInfoForm from '../pages/admin/cases/component/CaseInfoForm';
import { badRecordBtnToolTipMsg, badStatusFileError, fileUploadingBtnToolTipMsg } from '../utils/constant';
import useCaseData from './useCaseData';
import { getHasCompletedMedicalOrDemandRequest, isTerminalRecordStatus } from '../utils/caseResultUtils';

const defaultClient = getDefaultClient();
const defaultDefendant = getDefaultDefendant();
const defaultCaseInfo = getDefaultCaseInfo();
const MEDICAL_SUMMARY_COMPLETION_POLL_MS = 30000;
const BAD_RECORD_POLL_MS = 10000;
const AI_PROVIDER_RESOLUTION_POLL_MS = 20000;

const getNewlyBadRecords = (caseRecords, knownBadStatusRecords) => caseRecords.filter(record => (
  record.status === CREATE_REQUEST_STATUS.BAD_RECORD &&
  record.s3UrlPath &&
  !(record.s3UrlPath in knownBadStatusRecords)
));

const useAddCaseForm = () => {

  const { LiabilityAnalysisForm: LiabilityAnalysisFormOverride, DEFAULT_CASE_TYPE_MT, validateLiability } = useProductContext();

  const getLiabilityAnalysisForm = useCallback(() => {
    if (LiabilityAnalysisFormOverride) {
      return LiabilityAnalysisFormOverride;
    }
    return LiabilityAnalysisForm;
  }, [LiabilityAnalysisFormOverride]);

  // Form step configuration map with enum keys
  const FORM_STEP_CONFIG = useMemo(() => ({
    [FORM_STEPS.CASE_INFO]: {
      component: CaseInfoForm,
      tabType: null,
    },
    [FORM_STEPS.LIABILITY]: {
      component: getLiabilityAnalysisForm(),
      tabType: 'defendant',
    },
    [FORM_STEPS.MEDICAL_PROVIDERS]: {
      component: MedicalProviderForm,
      tabType: 'client',
    },
    [FORM_STEPS.PAIN_AND_SUFFERING]: {
      component: PainAndSufferingAnalysisForm,
      tabType: 'client',
    },
    [FORM_STEPS.ECONOMIC_DAMAGE]: {
      component: EconomicDamageAnalysisForm,
      tabType: 'client',
    },
  }), [getLiabilityAnalysisForm]);


  const { caseId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const { isEmbedded, sendMessageToParent } = useEmbedded();
  const { postData: pfqPostData } = usePfqAxios();
  const { updateCase, debounceUpdateCase } = useCaseFieldUpdate({ caseId });

  // Redux state
  const presignedUrlGenerationCount = useSelector(state => state.fileUpload.presignedUrlGenerationCount);
  const { isDelayedPayment } = useSelector((state) => state.users || {});
  const proccessedMedicalRecordsStored = useSelector(state => state.preProcessMedicalRecords.injuryData);
  const isPreProcessRecordLoading = proccessedMedicalRecordsStored?.isPreProcessRecordLoading;

  const resolvedCaseInfo = DEFAULT_CASE_TYPE_MT
    ? { ...defaultCaseInfo, caseType: DEFAULT_CASE_TYPE_MT }
    : defaultCaseInfo;

  // Formik - single source of truth (no Redux dependency)
  const formik = useFormik({
    initialValues: {
      caseInfo: resolvedCaseInfo,
      clients: [defaultClient],
      defendants: [defaultDefendant],
      liability: defaultLiability,
      teamIds: [],
      isIcdIncluded: true,
      isDraftCase: false,
      badStatusRecords: {}
    },
  });

  // Local state
  const [addCaseFormStep, setAddCaseFormStep] = useState(FORM_STEPS.CASE_INFO);
  const [addCaseIsLoading, setAddCaseIsLoading] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isWaitingForPresignedUrls, setIsWaitingForPresignedUrls] = useState(false);
  const [isFetchingCase, setIsFetchingCase] = useState(!!caseId);
  const [selectedTiffIndex, setSelectedTiffIndex] = useState(0);
  const [selectedDefendant, setSelectedDefendant] = useState(0);
  const [fileErrorList, setFileErrorList] = useState([]);
  const [isDisableSubmitButton, setDisableSubmitButton] = useState(false);
  const [submitBtnToolTipMsg, setSubmitBtnToolTipMsg] = useState(null);
  const [isShowReorderSec, setIsShowReorderSec] = useState({
    postAccident: false,
    preAccident: false
  });
  const [isOpenProcessedMedicalRecordsModal, setIsOpenProcessedMedicalRecordsModal] = useState(false);
  const [generateSummaryUpdateTime, setGenerateSummaryUpdateTime] = useState(null);
  const [createRequests, setCreateRequests] = useState({});
  const fileUploadState = useSelector(state => state.fileUpload);
  const [processCount, setProcessCount] = useState(0);
  const isBadRecordExist = useMemo(() => {
    const badRecords = formik.values.badStatusRecords
    if (!badRecords) return false;
    return Object.keys(badRecords).length > 0;
  }, [formik.values.badStatusRecords]);


  const isFilesUploading = useMemo(() => checkIsfileUploading(fileUploadState), [fileUploadState]);

  // an easy-mode case regenerates by restarting the assistant; a classic case regenerates the demand
  // directly. Both supersede whatever is in flight rather than being blocked, so the behaviour matches.
  const isEasyModeCase = Boolean(createRequests?.[REQUEST_TYPES.VIRTUAL_USER]);

  useEffect(() => {
    const shouldDisableSubmit = isFilesUploading || isBadRecordExist;
    setDisableSubmitButton(shouldDisableSubmit);
    if (isFilesUploading) {
      setSubmitBtnToolTipMsg(fileUploadingBtnToolTipMsg);
    } else if (isBadRecordExist) {
      setSubmitBtnToolTipMsg(badRecordBtnToolTipMsg);
    } else {
      setSubmitBtnToolTipMsg(null);
    }
  }, [isFilesUploading, isBadRecordExist]);


  const flagNewlyBadRecords = useCallback((caseRecords = []) => {
    formik.setValues(prevValues => {
      const newlyBadRecords = getNewlyBadRecords(caseRecords, prevValues.badStatusRecords);
      if (newlyBadRecords.length === 0) return prevValues;

      newlyBadRecords.forEach(() => errorToast(badStatusFileError));

      return {
        ...prevValues,
        badStatusRecords: {
          ...prevValues.badStatusRecords,
          ...Object.fromEntries(newlyBadRecords.map(record => [record.s3UrlPath, record]))
        }
      };
    });
  }, [formik.setValues]);

  // Handlers for preprocessing medical records
  const { fetchCaseData } = useCaseData({ caseId, });

  const applyMedicalSummaryRefresh = useCallback(async () => {
    const freshCaseData = await fetchCaseData();
    if (!freshCaseData) return;
    const clientRecords = freshCaseData.clients?.map((client) => ({
      clientId: client?.clientId,
      medicalRecords: client?.medicalProviders?.postAccident?.result?.medicalRecords?.flat(),
      preMedicalRecords: client?.medicalProviders?.preAccident?.result?.medicalRecords?.flat(),
      medicalBillRecords: client?.medicalProviders?.postAccident?.result?.medicalBillRecords,
    }));
    dispatch(preProcessedMedicalRecordsData({ isPreProcessRecordLoading: false, medicalProcessedData: clientRecords }));
  }, [fetchCaseData, dispatch]);

  const applyMedicalSummaryOutcome = useCallback(async (request) => {
    if (request?.requestType !== REQUEST_TYPES.MEDICAL_SUMMARY) return;
    if (request?.status === CREATE_REQUEST_STATUS.FAILED) {
      dispatch(preProcessedMedicalRecordsData({ isPreProcessRecordLoading: false }));
      return;
    }
    if (request?.status !== CREATE_REQUEST_STATUS.COMPLETED) return;
    try {
      await applyMedicalSummaryRefresh();
    } catch (error) {
      console.error('[socket] error refetching case after medical summary:', error);
    }
  }, [dispatch, applyMedicalSummaryRefresh]);

  const pollMedicalSummaryStatus = useCallback(async () => {
    const freshCaseData = await fetchCaseData();
    await applyMedicalSummaryOutcome(freshCaseData?.createRequests?.[REQUEST_TYPES.MEDICAL_SUMMARY]);
  }, [fetchCaseData, applyMedicalSummaryOutcome]);

  usePolling({
    intervalMs: MEDICAL_SUMMARY_COMPLETION_POLL_MS,
    callback: pollMedicalSummaryStatus,
    enabled: isPreProcessRecordLoading,
  });

  const [hasNonTerminalRecord, setHasNonTerminalRecord] = useState(false);

  const pollRecordStatus = useCallback(async () => {
    const freshCaseData = await fetchCaseData();
    if (!freshCaseData) return;
    const caseRecords = freshCaseData.caseRecords || [];
    setHasNonTerminalRecord(caseRecords.some(record => !isTerminalRecordStatus(record.status)));
    flagNewlyBadRecords(caseRecords);
  }, [fetchCaseData, flagNewlyBadRecords]);

  usePolling({
    intervalMs: BAD_RECORD_POLL_MS,
    callback: pollRecordStatus,
    // stay on through an in-flight upload too, so the record it creates is caught on the very next tick
    enabled: hasNonTerminalRecord || isFilesUploading,
  });

  // Fetch case details if editing (formik is stable, no need in deps)
  const cloneFiles = useCallback((files = []) => files.map((file) => ({ ...file })), []);
  const cloneRecords = useCallback((records = []) => (
    records.map((provider) => ({
      ...provider,
      source: Array.isArray(provider?.source) ? cloneFiles(provider.source) : provider?.source
    }))
  ), [cloneFiles]);

  const normalizeMedicalProviders = useCallback((client) => {
    const currentProviders = client?.medicalProviders || defaultMedicalProviders;
    const postRecords = cloneRecords(currentProviders?.postAccident?.medicalRecords || []);
    const preRecords = cloneRecords(currentProviders?.preAccident?.medicalRecords || []);

    const normalizedClient = {
      ...client,
      medicalProviders: {
        ...currentProviders,
        postAccident: {
          ...(currentProviders?.postAccident || {}),
          medicalRecords: postRecords
        },
        preAccident: {
          ...(currentProviders?.preAccident || {}),
          medicalRecords: preRecords
        }
      }
    };

    normalizedClient.medicalProvidersInputMode = client?.medicalProvidersInputMode
      ?? deriveMedicalProvidersInputMode({ client: normalizedClient });

    return normalizedClient;
  }, [cloneRecords]);

  const applyResolvedProviders = useCallback((freshClients) => {
    formik.values.clients?.forEach((currentClient, index) => {
      const freshClient = freshClients[index];
      if (!freshClient) return;
      const normalized = normalizeMedicalProviders(freshClient);
      // write only the locked sections; an unlocked sibling may hold edits the user is still typing
      getPendingAiResolutionSections(currentClient).forEach((section) => {
        formik.setFieldValue(
          `clients.${index}.medicalProviders.${section}.medicalRecords`,
          normalized.medicalProviders[section].medicalRecords
        );
      });
    });
  }, [formik, normalizeMedicalProviders]);

  const pollAiProviderResolution = useCallback(async () => {
    const freshCaseData = await fetchCaseData();
    if (!freshCaseData) return;
    const freshClients = freshCaseData.clients || [];
    applyResolvedProviders(freshClients);
    setCreateRequests(freshCaseData.createRequests || {});
    if (!freshClients.some(getIsClientPendingAiResolution)) successToast('Medical providers updated');
  }, [fetchCaseData, applyResolvedProviders]);

  const isAnyProviderListLocked = useMemo(
    () => (formik.values.clients || []).some(getIsClientPendingAiResolution),
    [formik.values.clients]
  );

  const isMedicalSummarySettled = useMemo(
    () => isSettledRequest(createRequests?.[REQUEST_TYPES.MEDICAL_SUMMARY]),
    [createRequests]
  );

  usePolling({
    intervalMs: AI_PROVIDER_RESOLUTION_POLL_MS,
    callback: pollAiProviderResolution,
    // AI can leave a record unnamed for good, so the summary request settling is what ends the poll
    enabled: isAnyProviderListLocked && !isMedicalSummarySettled,
  });

  useEffect(() => {
    const loadCase = async () => {
      if (!caseId) return;
      setIsFetchingCase(true);
      try {
        const caseDetails = await fetchCaseData();
        if (!caseDetails) {
          toaster({ message: 'Failed to load case', success: false });
          navigate("/cases");
          return;
        }

        const {
          caseInfo,
          clients,
          defendants,
          liability,
          teamIds,
          isIcdIncluded,
          isDraftCase,
          caseRecords,
          createRequests: latestCreateRequests
        } = caseDetails;
        setCreateRequests(latestCreateRequests || {});

        defendants.forEach((defendant) => {
          if (!getDefendantId(defendant)) setDefendantId(defendant, null);
        });
        const badStatusRecords = {};
        caseRecords?.forEach(record => {
          if (record.status === CREATE_REQUEST_STATUS.BAD_RECORD && record.s3UrlPath) {
            badStatusRecords[record.s3UrlPath] = record;
          }
        });
        setHasNonTerminalRecord((caseRecords || []).some(record => !isTerminalRecordStatus(record.status)));

        clients.forEach((client) => {
          if (!client.clientId) client.clientId = generateRandomIds();
        });
        const normalizedClients = clients.map(normalizeMedicalProviders);
        formik.setValues({
          caseInfo, clients: normalizedClients, defendants, liability,
          teamIds, isIcdIncluded, badStatusRecords,
          isDraftCase: isDraftCase || false
        });


        const latestMedicalSummaryRequest = latestCreateRequests[REQUEST_TYPES.MEDICAL_SUMMARY];

        setGenerateSummaryUpdateTime(latestMedicalSummaryRequest?.createdAt);

        if (latestMedicalSummaryRequest?.status === CREATE_REQUEST_STATUS.FAILED) {
          errorToast("There was an error generating the medical summary. Please try again later.");
        }

        dispatch(preProcessedMedicalRecordsData({
          isPreProcessRecordLoading: (latestMedicalSummaryRequest?.status === CREATE_REQUEST_STATUS.IN_PROGRESS || latestMedicalSummaryRequest?.status === CREATE_REQUEST_STATUS.PENDING) ? true : false,
        }));

        setProcessCount(caseDetails.processCount || 0);
      } catch (error) {
        console.error('Error fetching case details:', error);
      } finally {
        setIsFetchingCase(false);
      }
    };

    loadCase();
  }, [caseId]);


  const toggleReorderSection = (providerType, value = !isShowReorderSec[providerType]) => {
    setIsShowReorderSec({ ...isShowReorderSec, [providerType]: value });
  };

  // Presigned URL generation count handlers
  const incrementPresignedUrlCount = useCallback((count = 1) => {
    dispatch(presignedUrlGenerationStart(count));
  }, [dispatch]);

  const decrementPresignedUrlCount = useCallback((count = 1) => {
    dispatch(presignedUrlGenerationComplete(count));
  }, [dispatch]);

  // Reset form to initial state
  const resetAddCaseForm = useCallback(() => {
    // Reset Formik to initial values
    formik.resetForm({
      values: {
        caseInfo: defaultCaseInfo,
        clients: [defaultClient],
        defendants: [defaultDefendant],
        liability: defaultLiability,
        teamIds: [],
        isIcdIncluded: true,
        isDraftCase: false
      }
    });

    // Reset local state
    setAddCaseFormStep(FORM_STEPS.CASE_INFO);
    setSelectedTiffIndex(0);
    setSelectedDefendant(0);
    setFileErrorList([]);
    setIsWaitingForPresignedUrls(false);
    setIsShowReorderSec({ postAccident: false, preAccident: false });

    // Clear remaining Redux state
    dispatch(preProcessedMedicalRecordsData({}));
  }, [formik, dispatch]);

  const backToCases = () => {
    navigate('/cases');
    resetAddCaseForm();
  }

  const fromStepValue = useCallback((value) => {
    if (presignedUrlGenerationCount > 0) {
      toaster({ message: "Please wait for files to finish uploading before changing steps", success: false });
      return;
    }

    // Ensure value is between FORM_STEPS.CASE_INFO and FORM_STEPS.ECONOMIC_DAMAGE
    const clampedValue = Math.max(FORM_STEPS.CASE_INFO, Math.min(value, FORM_STEPS.ECONOMIC_DAMAGE));
    setAddCaseFormStep(clampedValue);
  }, [presignedUrlGenerationCount]);

  const incrementAddCaseFormStep = useCallback(() => {
    fromStepValue(addCaseFormStep + 1);
  }, [addCaseFormStep, fromStepValue]);

  const decrementAddCaseFormStep = useCallback(() => {
    fromStepValue(addCaseFormStep - 1);
  }, [addCaseFormStep, fromStepValue]);

  // Comprehensive validation function for form submission using Formik
  const validate = useCallback(() => {
    // Check if files are still uploading
    if (presignedUrlGenerationCount > 0) {
      return {
        isValid: false,
        shouldWait: true,
        error: "Please wait for files to finish uploading"
      };
    }

    // Validate liability selection from Formik
    const accidentSceneFiles = formik.values.liability?.accidentSceneFiles;
    if (accidentSceneFiles) {
      for (const file of accidentSceneFiles) {
        if (file.isLiability && file.defendants.length === 0) {
          return {
            isValid: false,
            shouldWait: false,
            error: `Please select at least one defendant for "${file.name}"`
          };
        }
      }
    }

    // Validate Letter of Protection for delayed payment accounts (only when on last step)
    const shouldValidateLOP = isDelayedPayment && isLastStep(addCaseFormStep);

    if (shouldValidateLOP) {
      const letterOfProtection = formik.values.caseInfo?.letterOfProtection;
      const hasRequiredFields = letterOfProtection?.s3UrlPath && letterOfProtection?.name;

      if (!hasRequiredFields) {
        return {
          isValid: false,
          shouldWait: false,
          error: "Please upload a Letter of Protection before generating the demand letter"
        };
      }
    }

    if (validateLiability && isLastStep(addCaseFormStep)) {
      const liabilityResult = validateLiability(formik.values);
      if (liabilityResult && !liabilityResult.isValid) {
        return { isValid: false, shouldWait: false, error: liabilityResult.error };
      }
    }

    return { isValid: true, shouldWait: false };
  }, [presignedUrlGenerationCount, formik.values.liability?.accidentSceneFiles, isDelayedPayment, addCaseFormStep, formik.values.caseInfo?.letterOfProtection, validateLiability, formik.values.liability?.massTortWaterAnalysis]);

  // Easy-mode cases regenerate by restarting the assistant, which supersedes the in-flight run and its
  // demand rather than being rejected for a run already going. The payload is the saved case, which the
  // classic editor persists as fields change, so it matches what the PFQ validates against.
  const restartEasyModeAssistant = useCallback(async () => {
    setAddCaseIsLoading(true);
    try {
      const response = await pfqPostData(
        '/virtual-user',
        JSON.stringify(buildVirtualUserPayload({
          caseId,
          intakeValues: {
            caseInfo: {
              caseName: formik.values.caseInfo?.caseName ?? '',
              caseType: formik.values.caseInfo?.caseType,
              stateOfIncident: formik.values.caseInfo?.stateOfIncident,
              dateOfIncident: formik.values.caseInfo?.dateOfIncident
            },
            plaintiffCount: formik.values.caseInfo?.plaintiffCount,
            defendantCount: formik.values.caseInfo?.defendantCount
          },
          additionalContext: formik.values.caseInfo?.additionalContext ?? '',
          forceRegenerate: true
        }))
      );

      if (!response?.success) {
        toaster({ message: response?.message ?? 'Could not restart the demand', success: false });
        return;
      }

      toaster({ message: 'Your demand is being regenerated', success: true });
      if (isEmbedded) {
        sendMessageToParent({ source: 'demandpro', type: 'demand:generate-clicked' });
      } else {
        navigate('/cases');
      }
    } catch (err) {
      console.log(err);
      toaster({ message: 'Could not restart the demand', success: false });
    } finally {
      setAddCaseIsLoading(false);
    }
  }, [caseId, formik.values.caseInfo, pfqPostData, isEmbedded, sendMessageToParent, navigate]);

  const generateDemand = useCallback(async () => {
    if (!caseId) {
      toaster({ message: 'Case ID is missing. Please save or reopen the case and try again.', success: false });
      return;
    }
    // easy-mode restarts the assistant instead of queueing a demand; a classic case posts a demand that the
    // PFQ supersedes over any run in flight - either way a run already going is no barrier
    if (isEasyModeCase) {
      await restartEasyModeAssistant();
      return;
    }
    setDisableSubmitButton(true);
    const validation = validate();
    if (!validation.isValid) {
      if (validation.shouldWait) {
        setIsWaitingForPresignedUrls(true);
      } else {
        setDisableSubmitButton(false);
        toaster({ message: validation.error, success: false });
      }
      return;
    }

    setAddCaseIsLoading(true);

    const payload = {
      caseId,
      requestType: REQUEST_TYPES.DEMAND
    };

    await pfqPostData(Constants.ApiUrl.createRequest, JSON.stringify(payload))
      .then(async (response) => {
        setAddCaseIsLoading(false);
        if (!response?.success) {
          toaster({ message: response.message, success: false });
        } else {
          toaster({ message: 'Demand letter generation initiated successfully', success: true });
          if (isEmbedded) {
            sendMessageToParent({
              source: "demandpro",
              type: "demand:generate-clicked"
            });
          } else {
            navigate("/cases");
          }
        }
      })
      .catch((err) => {
        console.log(err);
        setAddCaseIsLoading(false);
        toaster({ message: 'Failed to generate demand letter', success: false });
      });
    setDisableSubmitButton(false);
  }, [caseId, isEasyModeCase, restartEasyModeAssistant, validate, pfqPostData, setAddCaseIsLoading, isEmbedded, navigate, sendMessageToParent]);

  // Watch for presigned URL generation completion and retry demand generation
  useEffect(() => {
    if (isWaitingForPresignedUrls && presignedUrlGenerationCount === 0) {
      setIsWaitingForPresignedUrls(false);
      generateDemand();
    }
  }, [presignedUrlGenerationCount, isWaitingForPresignedUrls, generateDemand]);

  const handleNextClick = useCallback(() => {
    if (isLastStep(addCaseFormStep)) {
      generateDemand();
    } else {
      incrementAddCaseFormStep();
    }
  }, [addCaseFormStep, generateDemand, incrementAddCaseFormStep]);

  // Field helper functions for form inputs
  const getFieldProps = useCallback((name, options = {}) => {
    const { type = 'text', useDebounce = true } = options;
    const value = getIn(formik.values, name);

    const onChange = (e) => {
      const newValue = type === 'checkbox' ? e.target.checked : e.target.value;

      formik.setFieldValue(name, newValue);

      if (useDebounce) {
        debounceUpdateCase({ data: { [name]: newValue } });
      } else {
        updateCase({ data: { [name]: newValue } });
      }
    };

    if (type === 'checkbox') {
      return {
        name,
        checked: value || false,
        onChange
      };
    }

    return {
      name,
      value: value || '',
      onChange
    };
  }, [formik, updateCase, debounceUpdateCase]);

  const getMultiSelectFieldProps = useCallback((name, options = {}) => {
    const { useDebounce = true } = options;
    const value = getIn(formik.values, name);

    const onChange = (newValues) => {

      formik.setFieldValue(name, newValues);

      if (useDebounce) {
        debounceUpdateCase({ data: { [name]: newValues } });
      } else {
        updateCase({ data: { [name]: newValues } });
      }
    };

    return {
      selectedValues: value ?? [],
      onChange
    };
  }, [formik, updateCase, debounceUpdateCase]);

  const updateLiabilityFilesDefendants = useCallback((defendants, liabilityOverride) => {
    const allDefIds = defendants.map(d => d.defendantId);
    const liability = liabilityOverride ?? formik.values.liability;
    const fileFields = Object.values(LIABILITY_FILES_KEY);
    const data = {};

    fileFields.forEach((fieldKey) => {
      const fieldArray = liability?.[fieldKey] || [];
      fieldArray.forEach((item, index) => {
        if (item) {
          formik.setFieldValue(`liability.${fieldKey}[${index}].defendants`, allDefIds);
          data[`liability.${fieldKey}.${index}.defendants`] = allDefIds;
        }
      });
    });

    const accidentSceneFiles = liability?.accidentSceneFiles || [];
    accidentSceneFiles.forEach((item, index) => {
      if (item) {
        formik.setFieldValue(`liability.accidentSceneFiles[${index}].defendants`, allDefIds);
        data[`liability.accidentSceneFiles.${index}.defendants`] = allDefIds;
      }
    });

    if (Object.keys(data).length > 0) {
      updateCase({ data });
    }
  }, [formik, updateCase]);

  // Validate medical provider names
  const validateMedicalProviders = useCallback(() => {
    const validateMedicalProviderNames = ({ medicalProviderArr, providerType }) => {
      const errors = [];
      if (medicalProviderArr?.length) {
        medicalProviderArr.forEach((provider, index) => {
          if (provider?.medicalRecords?.length) {
            if (!provider.providerName || provider.providerName.trim() === '') {
              errors.push(`${providerType} record #${index + 1} is missing provider name`);
            }
          }
        });
      }
      return errors;
    };

    const clients = formik.values.clients || [];
    for (let i = 0; i < clients.length; i++) {
      const client = clients[i];
      const postErrors = validateMedicalProviderNames({
        medicalProviderArr: client.medicalProviders?.postAccident?.medicalRecords,
        providerType: 'Post Accident'
      });
      const preErrors = validateMedicalProviderNames({
        medicalProviderArr: client.medicalProviders?.preAccident?.medicalRecords,
        providerType: 'Pre Accident'
      });
      const errors = [...postErrors, ...preErrors];

      if (errors.length > 0) {
        toaster({ message: `Plaintiff ${i + 1}: ${errors[0]}`, success: false });
        return false;
      }
    }
    return true;
  }, [formik.values.clients]);

  // Set medical records loading state
  const setPreProcessMedicalRecords = useCallback((isLoading) => {
    dispatch(preProcessedMedicalRecordsData({
      ...proccessedMedicalRecordsStored,
      isPreProcessRecordLoading: isLoading,
    }));
  }, [dispatch, proccessedMedicalRecordsStored]);

  // Generate medical summary
  const generateMedicalSummary = useCallback(async ({ skipConfirm = false } = {}) => {
    if (!validateMedicalProviders()) {
      return;
    }

    if (!skipConfirm) {
      const ok = await confirm({
        title: "Medical Summary",
        description: "A credit will be deducted for this case. Future demand generation will not deduct additional credits.",
        confirmText: "Continue",
        cancelText: "Cancel",
      });
      if (!ok) return;
    }

    setGenerateSummaryUpdateTime(new Date());
    setPreProcessMedicalRecords(true);

    if (!caseId) {
      toaster({ message: 'Case ID is missing. Please save the case first.', success: false });
      return;
    }

    try {
      const payload = { caseId, requestType: REQUEST_TYPES.MEDICAL_SUMMARY };
      await pfqPostData(Constants.ApiUrl.createRequest, JSON.stringify(payload))
        .then((response) => {
          if (!response?.success) {
            toaster({ message: response.message || 'Failed to generate medical summary', success: false });
            setPreProcessMedicalRecords(false);
          } else {
            toaster({ message: 'Medical summary generation initiated successfully', success: true });
          }
        })
        .catch((err) => {
          console.error('Error requesting medical summary:', err);
          toaster({ message: 'Failed to generate medical summary', success: false });
          setPreProcessMedicalRecords(false);
        });
    } catch (error) {
      console.error('Error in generateMedicalSummary:', error);
      toaster({ message: 'Failed to process request', success: false });
      setPreProcessMedicalRecords(false);
    }
  }, [caseId, pfqPostData, validateMedicalProviders, setPreProcessMedicalRecords, confirm]);


  const isBadRecord = useMemo(() => {
    const badStatusRecords = formik.values.badStatusRecords;
    return (s3Path) => {
      if (!s3Path) return false;
      return s3Path in badStatusRecords;
    };
  }, [formik.values.badStatusRecords]);


  const removePathFromBadRecords = (s3Paths) => {
    if (!s3Paths) return;
    const pathsArray = Array.isArray(s3Paths) ? s3Paths : [s3Paths];
    const badRecordPaths = pathsArray.filter(path => isBadRecord(path));
    if (badRecordPaths.length === 0) return;
    const currentBadRecords = formik.values.badStatusRecords;
    const updatedBadStatusRecords = { ...currentBadRecords };
    badRecordPaths.forEach(path => {
      delete updatedBadStatusRecords[path];
    });
    formik.setFieldValue('badStatusRecords', updatedBadStatusRecords);
  };

  // Current step configuration
  const currentStepConfig = FORM_STEP_CONFIG[addCaseFormStep];
  const FormComponent = currentStepConfig?.component;
  const showClientTabs = currentStepConfig?.tabType === 'client' && formik.values.clients?.length > 1;
  const showDefendantTabs = currentStepConfig?.tabType === 'defendant' && formik.values.defendants?.length > 1;

  const hasCompletedMedicalOrDemandRequest = useMemo(() =>
    getHasCompletedMedicalOrDemandRequest(createRequests),
    [createRequests]
  );

  return {
    // Formik instance
    formik,

    // State
    isLoading: isFetchingCase,
    addCaseIsLoading,
    addCaseFormStep,
    isChatOpen,
    setIsChatOpen,
    caseId,
    isWaitingForPresignedUrls,
    presignedUrlGenerationCount,
    selectedTiffIndex,
    setSelectedTiffIndex,
    selectedDefendant,
    setSelectedDefendant,
    fileErrorList,
    setFileErrorList,
    isShowReorderSec,
    toggleReorderSection,
    processCount,

    // Step configuration
    currentStepConfig,
    FormComponent,
    showClientTabs,
    showDefendantTabs,

    // Handlers
    backToCases,
    resetAddCaseForm,
    fromStepValue,
    handleNextClick,
    generateDemand,
    incrementAddCaseFormStep,
    decrementAddCaseFormStep,
    incrementPresignedUrlCount,
    decrementPresignedUrlCount,

    // Field helpers
    getFieldProps,
    getMultiSelectFieldProps,
    debounceUpdateCase,
    updateCase,
    updateLiabilityFilesDefendants,

    // Config
    FORM_STEP_CONFIG,

    // Medical summary modal state
    isOpenProcessedMedicalRecordsModal,
    openMedicalSummaryModal: () => setIsOpenProcessedMedicalRecordsModal(true),
    closeMedicalSummaryModal: () => setIsOpenProcessedMedicalRecordsModal(false),

    // Medical summary
    generateSummaryUpdateTime,
    hasCompletedMedicalOrDemandRequest,
    setGenerateSummaryUpdateTime,
    validateMedicalProviders,
    generateMedicalSummary,

    isBadRecord,
    removePathFromBadRecords,
    isDisableSubmitButton,
    submitBtnToolTipMsg,

    // Latest CreateRequest per requestType (e.g. createRequests.SILENT_WITNESS)
    createRequests,
    setCreateRequests
  };
};

export default useAddCaseForm;
